import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { DeviceTypeResponse, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EMPTY_GUID, EnumToComboValues } from '../utils';
import { ICONS } from '../icons';
import { DeviceTypeDetailsSectionHandler } from './details.section';
import { AssetGroupingService } from 'app/services';
import { RegisterEntityHandler } from '../../entities';
import { InputTypeValues, DeviceTypeIoSectionHandler } from './io.section';
import { DeviceTypeCachedIoSectionHandler } from './cached.section';
import { DeviceTypeFeaturesSectionHandler } from './features.section';


@RegisterEntityHandler('devicetype')
@Injectable()
export class DeviceTypeHandler extends EntityHandler<DeviceTypeResponse> {

    currentEntity: DeviceTypeResponse;

    constructor(
        admin: AdminEditorService,
        private grouping: AssetGroupingService
    ) {
        super(admin);
        this.listMode = 'tree';
    }

    states = ['active', 'deleted'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        // we can't paginate tree items, so hard code a limit of 1024 and hope it's enough
        offset = 0;
        limit = 1024;
        return this.admin.getCached(`listDeviceTypes/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listDeviceTypes(ownerId, false, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'devicetype',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.DEVICETYPES'),
        }));
    }

    getEntity(id: string): Promise<DeviceTypeResponse> {
        return this.admin.getCached(`getDeviceType/${id}`, api => api.entities.getDeviceType(id), 10).then(result => {
            this.currentEntity = result;
            return result;
        });
    }

    createEntity(ownerId: string, entity: any): Promise<DeviceTypeResponse> {
        entity.ownerId = ownerId;
        this.grouping.clear(); // clear our grouping cache after an update
        return this.admin.applyUpdate(api => api.entities.createDeviceType(entity));
    }

    setEntityState(entity: DeviceTypeResponse, state: string): Promise<DeviceTypeResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDeviceType(entity.id, { state: state as any }));
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.DEVICETYPE.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', (row) => {
                return row.folder ? ICONS.folder : ICONS.device;
            }),
            grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 100),
            grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
        ];
        return Promise.resolve(columns);
    }

    async getEditorSections(ownerId: string, entity: any): Promise<SectionHandlerGroup<DeviceTypeResponse>[]> {
        return [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new DeviceTypeDetailsSectionHandler(this.admin, ownerId, this.grouping, false),
                entity && entity.parent && new DeviceTypeCachedIoSectionHandler(this.admin, ownerId),
                entity && entity.parent && new DeviceTypeFeaturesSectionHandler(this.admin, ownerId),
            ].filter(x => x),
        },
        entity && entity.parent && {
            name: this.admin.i18n.instant('ADMIN.EDITOR.IO'),
            sections: [
                ...InputTypeValues.map(iotype => new DeviceTypeIoSectionHandler(this.admin, ownerId, iotype)),
            ],
        }].filter(x => x);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<DeviceTypeResponse, any>[] }> {

        // find the last selected parent, only if the ownerId matches
        let parent: IdName = { id: EMPTY_GUID, name: this.admin.i18n.instant('SHARED.ROOT') };
        if (this.currentEntity && this.currentEntity.owner.id === ownerId) {

            if (this.currentEntity.parent === null) {
                parent = { id: this.currentEntity.id, name: this.currentEntity.name };
            } else {
                parent = { id: this.currentEntity.parent.id, name: this.currentEntity.parent.name };
            }
        }

        return Promise.resolve({
            entityType: 'devicetype',
            entity: {
                parent: parent,
            },
            sections: [
                new DeviceTypeDetailsSectionHandler(this.admin, ownerId, this.grouping, true),
            ],
        });
    }
}














